import type {
  Annotations,
  POI,
  Pin,
  Frame,
} from "../api/types";

export type SaveState =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "conflict"
  | "failed"
  | "pending"
  | "uploading"
  | "auth-expired";

export interface StoreApi {
  put(
    flightId: string,
    body: Annotations,
    ifMatch: string | null,
  ): Promise<{ kind: "ok"; etag: string } | { kind: "conflict" } | { kind: "error"; message: string; status: number }>;
}

export interface StoreOpts {
  flightId: string;
  team: string;
  initial: Annotations | null;
  initialEtag: string | null;
  api: StoreApi;
  debounceMs?: number;
  now?: () => string;
  /** When false, the store never PUTs on its own — an external writer (the
   *  OfflineQueue) owns all uploads. Defaults to true. */
  autoFlush?: boolean;
}

export type Mutation =
  | { kind: "upsertPoi"; poi: POI }
  | { kind: "deletePoi"; id: string }
  | { kind: "upsertPin"; pin: Pin }
  | { kind: "deletePin"; id: string }
  | { kind: "upsertFrame"; frame: Frame }
  | { kind: "deleteFrame"; id: string };

export interface PendingMutation { m: Mutation; clientTs: string; }

export class AnnotationStore {
  private annotations: Annotations;
  private etag: string | null;
  private saveState: SaveState = "idle";
  private dirty = false;
  private pending: PendingMutation[] = [];
  private persistedCursor = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();
  private readonly debounceMs: number;
  private readonly now: () => string;
  private readonly autoFlush: boolean;

  constructor(private readonly opts: StoreOpts) {
    this.debounceMs = opts.debounceMs ?? 2000;
    this.autoFlush = opts.autoFlush ?? true;
    this.now = opts.now ?? (() => new Date().toISOString());
    this.annotations =
      opts.initial ?? {
        schemaVersion: 1,
        flightId: opts.flightId,
        team: opts.team,
        updatedAt: this.now(),
        pois: [], pins: [], frames: [],
      };
    this.etag = opts.initialEtag;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  snapshot(): { annotations: Annotations; etag: string | null; saveState: SaveState } {
    return { annotations: this.annotations, etag: this.etag, saveState: this.saveState };
  }

  hasPendingMutations(): boolean {
    return this.pending.length > 0;
  }

  /**
   * Hand back pending mutations the offline layer hasn't seen yet. Each
   * mutation is yielded exactly once. The cursor resets if `pending` shrinks
   * (e.g. successful flush), so re-emerging mutations after a conflict reset
   * are surfaced again.
   */
  drainPendingForPersistence(): readonly PendingMutation[] {
    if (this.persistedCursor > this.pending.length) this.persistedCursor = 0;
    const slice = this.pending.slice(this.persistedCursor);
    this.persistedCursor = this.pending.length;
    return slice;
  }

  // ── Mutations ────────────────────────────────────────────────────────────
  upsertPoi(poi: POI): void { this.applyMutation({ kind: "upsertPoi", poi }); }
  deletePoi(id: string): void { this.applyMutation({ kind: "deletePoi", id }); }
  upsertPin(pin: Pin): void { this.applyMutation({ kind: "upsertPin", pin }); }
  deletePin(id: string): void { this.applyMutation({ kind: "deletePin", id }); }
  upsertFrame(frame: Frame): void { this.applyMutation({ kind: "upsertFrame", frame }); }
  deleteFrame(id: string): void { this.applyMutation({ kind: "deleteFrame", id }); }

  private applyMutation(m: Mutation): void {
    const clientTs = this.now();
    this.pending.push({ m, clientTs });
    this.annotations = applyMutationsTo(this.annotations, [{ m, clientTs }], this.now);
    this.dirty = true;
    if (this.saveState !== "saving") this.saveState = "dirty";
    if (this.autoFlush) this.scheduleFlush();
    this.notify();
  }

  private scheduleFlush(): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => { this.timer = null; void this.flushNow(); }, this.debounceMs);
  }

  async flushNow(): Promise<void> {
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
    if (!this.dirty) return;
    this.saveState = "saving";
    this.notify();
    try {
      const result = await this.opts.api.put(this.opts.flightId, this.annotations, this.etag);
      if (result.kind === "ok") {
        this.etag = result.etag;
        this.dirty = false;
        this.pending = [];
        this.persistedCursor = 0;
        this.saveState = "saved";
      } else if (result.kind === "conflict") {
        this.saveState = "conflict";
      } else {
        this.saveState = "failed";
      }
    } catch {
      // StoreApi.put is contractually non-throwing; this is defensive so the
      // UI can never get permanently stuck on "saving" if that contract breaks.
      this.saveState = "failed";
    }
    this.notify();
  }

  applyServerState(server: Annotations, serverEtag: string | null): void {
    // Replay our pending mutations on top of the authoritative server state.
    this.annotations = applyMutationsTo(server, this.pending, this.now);
    this.etag = serverEtag;
    if (this.pending.length > 0) {
      this.dirty = true;
      this.saveState = "dirty";
      if (this.autoFlush) this.scheduleFlush();
    } else {
      this.dirty = false;
      this.saveState = "saved";
    }
    this.notify();
  }

  private notify(): void { this.listeners.forEach((fn) => fn()); }
}

function applyMutationsTo(
  base: Annotations,
  mutations: readonly PendingMutation[],
  now: () => string,
): Annotations {
  let pois = base.pois.slice();
  let pins = base.pins.slice();
  let frames = base.frames.slice();
  for (const { m, clientTs } of mutations) {
    switch (m.kind) {
      case "upsertPoi": {
        const existing = pois.find((p) => p.id === m.poi.id);
        if (existing && existing.modifiedAt > clientTs) break; // server-newer guard
        const i = pois.findIndex((p) => p.id === m.poi.id);
        if (i >= 0) pois[i] = m.poi; else pois.push(m.poi);
        break;
      }
      case "deletePoi": {
        const existing = pois.find((p) => p.id === m.id);
        if (existing && existing.modifiedAt > clientTs) break;
        pois = pois.filter((p) => p.id !== m.id);
        break;
      }
      case "upsertPin": {
        const existing = pins.find((p) => p.id === m.pin.id);
        if (existing && existing.modifiedAt > clientTs) break;
        const i = pins.findIndex((p) => p.id === m.pin.id);
        if (i >= 0) pins[i] = m.pin; else pins.push(m.pin);
        break;
      }
      case "deletePin": {
        const existing = pins.find((p) => p.id === m.id);
        if (existing && existing.modifiedAt > clientTs) break;
        pins = pins.filter((p) => p.id !== m.id);
        break;
      }
      case "upsertFrame": {
        const existing = frames.find((f) => f.id === m.frame.id);
        if (existing && existing.capturedAt && existing.capturedAt > clientTs) break;
        const i = frames.findIndex((f) => f.id === m.frame.id);
        if (i >= 0) frames[i] = m.frame; else frames.push(m.frame);
        break;
      }
      case "deleteFrame": {
        const existing = frames.find((f) => f.id === m.id);
        if (existing && existing.capturedAt && existing.capturedAt > clientTs) break;
        frames = frames.filter((f) => f.id !== m.id);
        break;
      }
    }
  }
  return { ...base, pois, pins, frames, updatedAt: now() };
}
